import React from "react";
import { Link } from "react-router-dom";
import { Building2, ScrollText, Users } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { RichTextView } from "@/components/forms/RichTextEditor";
import { OrgIdentity } from "@/components/groups/GroupAffiliations";
import { STICKY_RAIL } from "@/components/groups/groupSticky";

const initials = (s) => String(s || "?").trim().slice(0, 2).toUpperCase();

/**
 * Batch C.1 — the Group CONTEXT rail (GRP-DEC-335).
 *
 * The persistent right-hand column of the Group page, in ONE fixed order that does not
 * change between tabs or breakpoints: **Organizers → Affiliated Organizations → Group
 * Rules**. It is the member-facing, read-only presentation; editing stays in Manage, and
 * affiliation MANAGEMENT stays in Manage → Affiliations.
 *
 * On a phone it is not squeezed beside the feed: the grid collapses to one column and the
 * rail reflows underneath the tab content in the same order.
 */
export function GroupContextRail({ group, organizers = [], affiliations = [] }) {
  const approved = (affiliations || []).filter((a) => a.state === "approved");
  return (
    <aside className={`min-w-0 space-y-3 ${STICKY_RAIL}`} data-testid="group-context-rail">
      <Card className="space-y-3 border-0 p-5">
        <h2 className="flex items-center gap-1.5 text-base font-semibold">
          <Users className="h-4 w-4 text-muted-foreground" aria-hidden="true" />Organizers
        </h2>
        {organizers.length ? (
          <div className="space-y-2" data-testid="group-organizers">
            {organizers.map((o) => (
              <div key={`${o.role}-${o.display_name}`} className="flex items-center gap-2">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src={o.headshot_url || undefined} alt="" />
                  <AvatarFallback>{initials(o.display_name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  {o.slug ? (
                    <Link to={`/p/${o.slug}`}
                          className="block truncate text-sm font-medium hover:text-teal-700">
                      {o.display_name}
                    </Link>
                  ) : <p className="truncate text-sm font-medium">{o.display_name}</p>}
                  <p className="text-xs capitalize text-muted-foreground">{o.role}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            The organizers keep their names private.
          </p>
        )}
      </Card>

      {/* GRP-DEC-330/335 — APPROVED affiliations only, directly under Organizers, in the
          canonical Business / Capital identity presentation. A pending or declined request
          never reaches this surface. */}
      {approved.length ? (
        <Card className="space-y-3 border-0 p-5" data-testid="group-rail-affiliations">
          <h2 className="flex items-center gap-1.5 text-base font-semibold">
            <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Affiliated Organizations
          </h2>
          <div className="space-y-2">
            {approved.map((a) => (
              <OrgIdentity key={a.id} org={a.org}
                           testId={`group-affiliation-public-${a.profile_id}`} />
            ))}
          </div>
        </Card>
      ) : null}

      {/* GRP-DEC-335 — the member-facing Rules presentation lives here and nowhere else.
          Same canonical sanitized `groups.rules` field; no second copy, no second field. */}
      {group.rules ? (
        <Card className="space-y-2 border-0 p-5" data-testid="group-rail-rules">
          <h2 className="flex items-center gap-1.5 text-base font-semibold">
            <ScrollText className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Group Rules
          </h2>
          <RichTextView html={group.rules} className="text-sm text-muted-foreground" />
        </Card>
      ) : null}
    </aside>
  );
}
